import { useAuth } from '../hooks/useAuthHook'
import { useEffect, useState } from 'react'
import type { ElementType } from 'react'
import { dashboardAPI } from '../lib/api'
import type { Announcement } from '../types'
import { BarChart3, Users, Calendar, DollarSign, TrendingUp, Clock, AlertCircle } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { useNavigate } from 'react-router-dom'

type StatCard = {
  name: string
  value: string
  change?: string
  icon: ElementType
  color: string
  bgGradient: string
}

interface RecentBooking {
  id: number
  start_time: string
  end_time: string
  status: string
  total_amount: number
  room_name: string
  customer_name: string
  customer_email: string
}

export function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [monthRevenue, setMonthRevenue] = useState<number>(0)
  const [totalCustomers, setTotalCustomers] = useState<number>(0)
  const [activeBookings, setActiveBookings] = useState<number>(0)
  const [utilRate, setUtilRate] = useState<number>(0)
  const [recent, setRecent] = useState<RecentBooking[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        setLoading(true)
        const response = await dashboardAPI.getData({ open_hours_per_day: 12 })

        if (mounted && response.success) {
          const data = response.data as {
            announcements: Announcement[]
            kpis: {
              total_customers: number
              active_bookings: number
              monthly_revenue: number
              utilization_rate: number
              unique_customers?: number
            }
            recent_bookings: RecentBooking[]
            rooms: any[]
            customer_count: number
          }

          setAnnouncements(data.announcements || [])
          setMonthRevenue(data.kpis.monthly_revenue || 0)
          setTotalCustomers(data.kpis.total_customers || 0)
          setActiveBookings(data.kpis.active_bookings || 0)
          setUtilRate(data.kpis.utilization_rate || 0)
          setRecent(data.recent_bookings || [])
        }
      } catch (error) {
        console.error('Failed to load dashboard:', error)
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => { mounted = false }
  }, [])

  const stats: StatCard[] = [
    {
      name: 'Total Customers',
      value: totalCustomers.toLocaleString(),
      change: undefined,
      icon: Users,
      color: 'text-primary-600 dark:text-primary-400',
      bgGradient: 'from-primary-50 to-primary-100 dark:from-primary-500/10 dark:to-primary-600/10'
    },
    {
      name: 'Active Bookings',
      value: activeBookings.toLocaleString(),
      change: undefined,
      icon: Calendar,
      color: 'text-accent-600 dark:text-accent-400',
      bgGradient: 'from-accent-50 to-accent-100 dark:from-accent-500/10 dark:to-accent-600/10'
    },
    {
      name: 'Monthly Revenue',
      value: `£${monthRevenue.toFixed(2)}`,
      change: undefined,
      icon: DollarSign,
      color: 'text-success-600 dark:text-success-400',
      bgGradient: 'from-success-50 to-success-100 dark:from-success-500/10 dark:to-success-600/10'
    },
    {
      name: 'Utilization Rate',
      value: `${Math.round(utilRate * 100)}%`,
      change: undefined,
      icon: TrendingUp,
      color: 'text-warning-600 dark:text-warning-400',
      bgGradient: 'from-warning-50 to-warning-100 dark:from-warning-500/10 dark:to-warning-600/10'
    },
  ]

  const getStatusBadge = (status: string) => {
    const badges = {
      confirmed: 'badge-success',
      cancelled: 'badge-error',
      pending: 'badge-warning',
      completed: 'badge-info',
    }
    return badges[status as keyof typeof badges] || 'badge-primary'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 animate-fade-slide-up">
      {/* Announcements */}
      {announcements.length > 0 && (
        <div className="card border-l-4 border-l-primary-500">
          <div className="card-header flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-primary-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Announcements</h3>
          </div>
          <div className="card-body space-y-3">
            {announcements.map(a => (
              <div key={a.id} className="p-4 rounded-xl border border-primary-100 dark:border-primary-900/30 bg-primary-50/30 dark:bg-primary-500/5">
                <div className="font-medium text-gray-900 dark:text-gray-100">{a.title}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap mt-1">{a.body}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Welcome header */}
      <div>
        <h1 className="text-3xl font-bold text-gradient-primary">
          Welcome back, {user?.name}!
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Here's what's happening with your studio today.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.name} className={`card bg-gradient-to-br ${stat.bgGradient} hover:scale-[1.02] transition-all duration-300 cursor-pointer`}>
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {stat.name}
                    </p>
                    <p className={`text-3xl font-bold ${stat.color}`}>
                      {stat.value}
                    </p>
                    {stat.change && (
                      <p className="text-sm font-semibold text-success-600 dark:text-success-400 mt-1">
                        {stat.change}
                      </p>
                    )}
                  </div>
                  <div className={`p-3 rounded-xl bg-white/50 dark:bg-gray-800/50`}>
                    <Icon className={`h-8 w-8 ${stat.color}`} />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Recent bookings */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Bookings</h3>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Customer</th>
                <th className="table-header-cell">Room</th>
                <th className="table-header-cell">Date & Time</th>
                <th className="table-header-cell">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {recent.length === 0 ? (
                <tr>
                  <td colSpan={4} className="table-cell text-center py-12 text-gray-500 dark:text-gray-400">
                    No recent bookings
                  </td>
                </tr>
              ) : (
                recent.map((booking) => (
                  <tr key={booking.id} className="table-row">
                    <td className="table-cell font-medium">
                      {booking.customer_name}
                    </td>
                    <td className="table-cell text-gray-600 dark:text-gray-300">
                      {booking.room_name}
                    </td>
                    <td className="table-cell text-gray-600 dark:text-gray-300">
                      {new Date(booking.start_time).toLocaleDateString()} at{' '}
                      {new Date(booking.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${getStatusBadge(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card hover:shadow-soft-lg transition-all duration-300 group">
          <div className="card-body text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-500/20 dark:to-primary-600/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Users className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Add Customer</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">Register a new customer to your studio</p>
            <Button onClick={() => navigate('/customers')} variant="primary">Add Customer</Button>
          </div>
        </div>

        <div className="card hover:shadow-soft-lg transition-all duration-300 group">
          <div className="card-body text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-accent-100 to-accent-200 dark:from-accent-500/20 dark:to-accent-600/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Calendar className="h-8 w-8 text-accent-600 dark:text-accent-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">New Booking</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">Schedule a new room booking</p>
            <Button onClick={() => navigate('/bookings')} variant="primary">Create Booking</Button>
          </div>
        </div>

        <div className="card hover:shadow-soft-lg transition-all duration-300 group">
          <div className="card-body text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-success-100 to-success-200 dark:from-success-500/20 dark:to-success-600/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <BarChart3 className="h-8 w-8 text-success-600 dark:text-success-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">View Reports</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">Analyze your studio performance</p>
            <Button onClick={() => navigate('/reports')} variant="primary">Open Reports</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
